import { Component } from '@angular/core';

import { IRuleSummary, ISummaryMetrics } from '../../../../../../../../shared/interfaces/scan-summaries.interface';
import { $ruleSummary, $scanSummaries } from '../../../../../../../../shared/constants/scan-summaries';
import { IAuditRule, WcagLevel } from '../../../../../../../../shared/interfaces/audit-rule.interface';
import { ISuccessCriteria } from '../../../../../../../../shared/audits/definitions/success-criteria/success-criteria.interface';
import { $successCriteria } from '../../../../../../../../shared/audits/definitions/success-criteria/constants';
import { AuditStandards } from '../../../../../../../../shared/constants/audit-standard';
import { $auditRule } from '../../../../../../../../shared/constants/audit-rule';
import { $severity } from '../../../../../../../../shared/constants/accessibility';
import { AuditTypes } from '../../../../../../../../shared/constants/audit-types';
import { AbstractIssuesByStandardComponent } from '../abstract-issues-by-standard.component';
import { SharedCommonUtility } from '../../../../../../../../shared/utils/common.utility';
import { TranslateService } from '../../../../../../translate/translate.service';
import { SuccessCriteriaService } from '../../../../../../services/success-criteria.service';

interface ILevelSeverity {
  level: WcagLevel;
  severity: $severity;
}

@Component({
  selector: 'app-issues-by-wcag-level',
  templateUrl: '../abstract-issues-by-standard.component.html',
  styleUrls: ['../abstract-issues-by-standard.component.scss'],
  standalone: false,
})
export class IssuesByWCAGLevelComponent extends AbstractIssuesByStandardComponent {
  constructor(
    translateService: TranslateService,
    private successCriteriaService: SuccessCriteriaService,
  ) {
    super(translateService);
  }

  private getRuleWcagLevel(ruleSummary: IRuleSummary): ILevelSeverity {
    const auditRule: IAuditRule | undefined = this.scanAudits[this.auditType]?.rules?.[ruleSummary.ruleName];

    if (SharedCommonUtility.isNullish(auditRule) || SharedCommonUtility.isNullishOrEmpty(auditRule[$auditRule.wcagCriteria])) {
      return null;
    }

    const levelReducer = (acc: WcagLevel | null, criteria: string): WcagLevel => {
      const successCriteria: ISuccessCriteria | null = this.successCriteriaService.getSuccessCriteriaFromStandard(
        AuditStandards.wcag,
        criteria,
      );
      if (SharedCommonUtility.isNullish(successCriteria)) {
        return acc;
      }
      const level: WcagLevel = successCriteria[$successCriteria.level] as WcagLevel;
      if (!Object.values(WcagLevel).includes(level)) {
        return acc;
      }
      if (SharedCommonUtility.isNullish(acc)) {
        return level;
      }
      return acc.length < level.length ? acc : level;
    };

    const ruleLevel: WcagLevel = auditRule[$auditRule.wcagCriteria].reduce<WcagLevel>(levelReducer, null);
    if (SharedCommonUtility.isNullish(ruleLevel)) {
      return null;
    }

    return { level: ruleLevel, severity: auditRule[$auditRule.severity] };
  }

  public calculateTable(): void {
    const metrics: ISummaryMetrics = this.scanSummary[$scanSummaries.metrics];

    const rulesEvaluatedByLevel: Record<WcagLevel, number> = {
      [WcagLevel.A]: 0,
      [WcagLevel.AA]: 0,
      [WcagLevel.AAA]: 0,
      [WcagLevel.CR]: 0,
    };

    const rulesErrorByLevel: Record<WcagLevel, number> = {
      [WcagLevel.A]: 0,
      [WcagLevel.AA]: 0,
      [WcagLevel.AAA]: 0,
      [WcagLevel.CR]: 0,
    };

    for (const ruleId of Object.keys(metrics)) {
      const ruleSummary: IRuleSummary = metrics[ruleId];
      const ruleLevelSeverity: ILevelSeverity = this.getRuleWcagLevel(ruleSummary);
      if (SharedCommonUtility.isNullish(ruleLevelSeverity)) {
        continue; // not a wcag rule
      }
      if (ruleSummary[$ruleSummary.triggered]) {
        if (ruleLevelSeverity.severity !== $severity.info) {
          if (this.auditType !== AuditTypes.accessibility_wave || ruleSummary[$ruleSummary.errors] > 0) {
            rulesEvaluatedByLevel[ruleLevelSeverity.level] += 1;
          }
          rulesErrorByLevel[ruleLevelSeverity.level] += ruleSummary[$ruleSummary.errors];
        } else if (ruleSummary[$ruleSummary.errors] > 0) {
          rulesEvaluatedByLevel[ruleLevelSeverity.level] += 1;
        }
      }
    }

    this.conformanceData = [];
    for (const level of Object.values(WcagLevel)) {
      if (!this.includeStandard.levels.includes(level)) {
        break;
      }
      if (rulesEvaluatedByLevel[level] !== 0 || rulesErrorByLevel[level] !== 0) {
        this.conformanceData.push({
          standard: `${this.includeStandard.standard} ${this.includeStandard.version} ${level}`,
          rulesEvaluated: rulesEvaluatedByLevel[level],
          issuesDiscovered: rulesErrorByLevel[level],
        });
      }
    }
    this.tableData = this.toTableData(this.conformanceData);
  }
}
